import type { TeamMemberDto } from '@api'
import type { DateRange } from 'react-day-picker'

import { CalendarDatePicker } from '@/components/ui/calendar/calendar-date-picker'
import { CalendarRangeDatePicker } from '@/components/ui/calendar/calendar-range-date-picker'
import type { Creator } from '@/modules/creators/types'
import { classNames } from '@/utils/helpers'

import { MultiSelect } from './dialogs/multi-select'

export interface FiltersFormValues {
  from: Date
  to: Date
  includedMemberIds: string[]
  includedCreatorIds: string[]
}

interface Props {
  onChange: (values: FiltersFormValues) => void
  values: FiltersFormValues
  className?: string
  includeDateRange?: boolean
  includeDatePicker?: boolean
  datePickerValue?: Date
  onChangeDatePicker?: (date?: Date) => void
  creators: Creator[]
  teamMembers: TeamMemberDto[]
  includeTeamMembers: boolean
}

export const ShiftsFilters = ({
  onChange,
  values,
  className,
  includeDatePicker,
  datePickerValue,
  onChangeDatePicker,
  includeDateRange,
  creators,
  teamMembers,
  includeTeamMembers,
}: Props) => {
  const teamMembersOptions =
    teamMembers?.map(({ id, name }) => ({
      label: name,
      value: String(id),
    })) ?? []

  const creatorsOptions =
    creators?.map(({ name, id }) => ({
      label: name,
      value: String(id),
    })) ?? []

  const handleChange = (
    name: keyof FiltersFormValues,
    value: FiltersFormValues[keyof FiltersFormValues],
  ) => {
    onChange({
      ...values,
      [name]: value,
    })
  }

  const setDate = (date: DateRange) => {
    if (date.from && date.to) {
      onChange({
        ...values,
        from: date.from,
        to: date.to,
      })
    }
  }

  return (
    <div
      className={classNames(
        'flex w-full flex-1 flex-col gap-4 sm:flex-row',
        className,
      )}
    >
      {includeTeamMembers && (
        <MultiSelect
          value={values.includedMemberIds}
          onChange={(value) => handleChange('includedMemberIds', value)}
          options={teamMembersOptions}
          entity="Team Members"
          placeholder="Team Member"
          searchPlaceholder="Search Team Members"
          className="w-full sm:max-w-[204px]"
        />
      )}
      <MultiSelect
        value={values.includedCreatorIds}
        onChange={(value) => handleChange('includedCreatorIds', value)}
        options={creatorsOptions}
        entity="Creators"
        placeholder="Creator"
        searchPlaceholder="Search Creator"
        className="w-full sm:max-w-[204px]"
      />
      {includeDateRange && (
        <CalendarRangeDatePicker
          className="w-full sm:max-w-[204px]"
          date={{ to: values.to, from: values.from }}
          setDate={setDate}
          maxDateRange={{
            to: new Date(),
            from: new Date(new Date().getFullYear() - 1, 1, 1),
          }}
        />
      )}
      {includeDatePicker && onChangeDatePicker && (
        <CalendarDatePicker
          className="w-full sm:max-w-[204px]"
          date={datePickerValue}
          onChange={onChangeDatePicker}
          placeholder="Select date"
        />
      )}
    </div>
  )
}
